import { GameState } from './GameState';
import { EventBus, GameEvents, NoticeHookPayload } from '../core/EventBus';
import { GameData } from '../data/GameData';
import { ASSISTANT } from '../data/Assistant';
import { GameConfig } from '../core/GameConfig';
import { Utils } from '../core/Utils';
import { bandOf } from '../core/RepBands';
import { AttributeKey, EventRequire, EventTrigger, FixedEventDef, FixedStep, NoticePlotHook, PlotBeatEffect, PlotChat, PlotHost, PlotHook, PlotMessage } from '../data/types';
import { normalNpcSystem } from './NormalNpcSystem';
import { dramaTimelineOf, scriptPremiereAbsOf } from './ScriptSystem';

/**
 * 剧情系统（PlotSystem，2026-10-06 批 2；设计基准 docs/特殊剧本实装设计.md §三）
 *
 * 两类事件源、一套检查循环、一套分发器：
 *  1. 全局事件：每月 checkPending 扫 FixedEvents 表 trigger（calendar 日历/咖位/数值/flag；带 location 的不进扫描，见下）；
 *  2. 剧本钩子：监听特殊剧本生命周期信号（SPECIAL_CAST/DONE，批 1 已 emit）→
 *     查 SpecialScripts.plotHooks → afterMonths 延迟入队 → 到期 cond 检查（默认过时不候）。
 *  另有通告钩子（2026-10-24 通告事件批，onNoticeHook）：通告生命周期信号（NOTICE_HOOK 一信号收
 *  所有通告类型×时点）→ 查 NOTICE_PLOT_HOOKS → 掷 chance → fire 带剧本上下文（插值 {script}/{role} 的来源）。
 *
 * 分发（三载体三呈现，2026-10-14 茄子定）：入队统一走 `player.plotMessages`，呈现按 medium 分流——
 *  - `encounter` → VN 通道（`src/ui/plotStage.ts` 行级对白解析；排干器 `src/ui/plotPlayback.ts` 三处宿主共用，
 *    2026-10-14 起三个宿主＝决策页、月度执行页、出行地图（地图那处含公园与咖啡馆，玩家看得见四处））；
 *  - `media` → MediaPanel 弹窗（热议帖/新闻）；
 *  - `message` → **人脉页聊天页的聊天消息**（月底 `sweepChatDelivery` 统一发送，落 `player.plotChats`
 *    存根；演出档见到 message 拍让位不推进——发送时机=月底，月度执行期间不弹卡）。
 * 演出宿主（2026-10-14 茄子令新增，读 `FixedEventDef.playOn`，缺省执行档）：`decision`＝决策页
 *  （点「确认行程」之后、颁奖夜之后、行程执行之前演，播出侧那类剧情走这里），`execution`＝月度执行页
 *  的演出档＋地图到访演出，`stageStory`＝阶段审查演出的全屏场景宿主（2026-10-21 加，决策日入场时演）。
 *  分派只在 `pendingFor` 一处判，宿主各写一遍就是第二把尺。
 * 地点型 trigger（`trigger.location`）不进月度日历扫描＝必须到访才派发（2026-10-13 茄子定），见 `checkPending` ②。
 * 2026-10-13 形状 C 收口：派发＝只入队，**结算（flag/结识/首步效果）跟着演出交付走**（`deliver`）。
 * 不许2026-10-06 那版「fire 里当场结算、句子等批 3 再播」＝形状 B「效果先结算、句子后播」，
 * 玩家会「人脉页凭空多一个人、剧情一个字读不到」，茄子 10-13 令治本。
 * 未读不许并入「人脉」胶囊红点（2026-10-11 茄子令：人脉页只管聊天页发的消息）。事件的一次性用 player.flags（交付时 setFlag(event.id)）。
 *
 * 多步剧情（steps）：收件箱条目持有 stepIndex，UI 逐步推进调 advancePlot()/choosePlot()；
 * 步级 requireFlags 不满足的步跳过（岔路表达），选项 setFlags 点灯。
 */
export class PlotSystem {
    private static instance: PlotSystem;
    static getInstance(): PlotSystem {
        if (!this.instance) this.instance = new PlotSystem();
        return this.instance;
    }
    private constructor() { }

    /** 延迟队列（内存态：afterMonths 未到期的钩子；跨存档丢失=错过该钩子，符合过时不候默认语义） */
    private deferred: { eventId: string; dueAbsMonth: number; hook: PlotHook }[] = [];

    /** 挂载：监听特殊剧本生命周期信号 + GameLoop.doFinishMonth 调 checkPending */
    init(): void {
        EventBus.on(GameEvents.SPECIAL_CAST, (scriptId: string) => this.onSpecialSignal('cast', scriptId));
        EventBus.on(GameEvents.SPECIAL_DONE, (scriptId: string) => this.onSpecialSignal('done', scriptId));
        EventBus.on(GameEvents.SPECIAL_AIR_START, (scriptId: string) => this.onSpecialSignal('airStart', scriptId));
        EventBus.on(GameEvents.SPECIAL_RATING, (scriptId: string) => this.onSpecialSignal('rating', scriptId));
        // 通告钩子（2026-10-24 通告事件批）：一个信号收所有通告类型×时点——加通告/加时点都不再来这里加行
        EventBus.on(GameEvents.NOTICE_HOOK, (p: NoticeHookPayload) => this.onNoticeHook(p));
    }

    /**
     * **通告钩子判定**（2026-10-24 通告事件批）：type×when 命中 → cond → 掷 chance → fire 带剧本上下文。
     * 一张表一处判定＝全库唯一口；与特殊剧本钩子（onSpecialSignal）互不代劳（那边是剧本专属叙事）。
     * fire 的剧本上下文落 PlotMessage，是 {script}/{role} 插值与「同事件不同剧」查重的数据源。
     * cond 两类判据（2026-10-24 同日加）：rank＝玩家态；billingMax＝剧上下文态（payload 带的角色反查番位，
     * 综艺类无番位＝不满足——「番位 ≤ N」只有带角色的通告才写得通）。
     */
    private onNoticeHook(p: NoticeHookPayload): void {
        // 候选＝type×when×cond 全过；**必发的排在前**（2026-11-03 茄子令「有满足条件固定触发的事件
        // 就不用再掷骰随机事件」）——它占了位，同一次信号里的概率条目连骰都不掷。判定仍只在这一处。
        const cands = GameData.NOTICE_PLOT_HOOKS
            .filter(h => h.type === p.type && h.when === p.when && this.noticeCondMet(h, p))
            .sort((a, b) => Number(a.chance !== undefined) - Number(b.chance !== undefined));
        for (const hook of cands) {
            // 「每周只触发一个」不新建计数：一周只有一个 work 槽，拍摄周与杀青天然错开（杀青周只发 done），
            // 一次信号＝一周的份额。月粒度另有一条跨信号去重，见 `monthHookFired`。
            if (hook.per === 'month' && this.monthHookFired()) continue;
            if (hook.chance !== undefined && Utils.randomFloat(0, 1) >= hook.chance) continue;
            const ev = this.eventById(hook.event);
            if (!ev) {
                console.warn(`[PlotSystem] 通告钩子 ${hook.when} 指向的事件 ${hook.event} 在 FixedEvents 表内查无，所以这条信号静默落空（有钩子没事件＝配错）`);
                continue;
            }
            if (this.fire(ev, { scriptId: p.refId, roleIndex: p.roleIndex })) break;
        }
    }

    /**
     * 通告钩子的条件判定（2026-11-03 起三类，各读一个数据源）：
     *  - `rank`＝玩家态；
     *  - `billingMax`＝payload 带的角色反查番位（无番位＝不满足）；
     *  - `genres`＝payload 的 refId 反查剧本类型（剧本查无＝不满足）。
     */
    private noticeCondMet(hook: NoticePlotHook, p: NoticeHookPayload): boolean {
        const state = GameState.getInstance();
        if (hook.cond?.rank !== undefined && state.player.rank < hook.cond.rank) return false;
        if (hook.cond?.billingMax !== undefined) {
            const role = GameData.SCRIPT_MAP[p.refId]?.roles[p.roleIndex ?? 0];
            if (!role?.billing || role.billing > hook.cond.billingMax) return false;
        }
        if (hook.cond?.genres) {
            const genre = GameData.SCRIPT_MAP[p.refId]?.genre;
            if (!genre || !hook.cond.genres.includes(genre)) return false;
        }
        return true;
    }

    /** 本月是否已派发过**月粒度**通告钩子（2026-11-03 茄子令「以月为单位的每月只触发一个」）：
     *  判据读 `PlotMessage.pushedAbsMonth` 现成字段：本月入过队就占了位，演完也不退还份额。
     *  禁止为此新增存档字段或升 SAVE_VERSION——队条目本来就随存档落盘，跨刷新同一个月读出来是同一个数。 */
    private monthHookFired(): boolean {
        const state = GameState.getInstance();
        const monthly = new Set(GameData.NOTICE_PLOT_HOOKS.filter(h => h.per === 'month').map(h => h.event));
        if (monthly.size === 0) return false;
        const abs = state.player.year * 12 + state.player.month;
        return (state.player.plotMessages ?? []).some(m => monthly.has(m.eventId) && m.pushedAbsMonth === abs);
    }

    /** 月度检查（GameLoop.doFinishMonth 挂载）：全局事件扫描 + 延迟队列到期处理 */
    checkPending(): void {
        const state = GameState.getInstance();
        const absMonth = state.player.year * 12 + state.player.month;

        // ① 延迟队列：到期钩子做 cond 检查（默认过时不候；wait=true 挂起继续等）
        const stillWaiting: typeof this.deferred = [];
        for (const d of this.deferred) {
            if (d.dueAbsMonth > absMonth) { stillWaiting.push(d); continue; }
            const ev = this.eventById(d.eventId);
            if (!ev) continue;
            if (this.condMet(d.hook.cond, d.eventId) || d.hook.wait === true) {
                if (this.condMet(d.hook.cond, d.eventId)) this.fire(ev);
                else stillWaiting.push(d);   // wait 挂起：条件未满足继续等
            }
            // 默认：作废跳过（过时不候）
        }
        this.deferred = stillWaiting;

        // ② 全局事件扫描（固定剧情／时间线剧情）：trigger 全满足且未触发过就 fire，**条数不限**。
        //    可重复数值事件不在这条链上（走 ②' 两道闸）；**一次性数值事件在这条链上**——
        //    「都一次性了基本上相当于特殊事件」（2026-10-26 茄子令）：必发、不占重复事件的总闸与
        //    每月一条名额、演完置灯不再发，语义与固定剧情完全一致。
        for (const ev of GameData.FIXED_EVENTS) {
            if (!ev.trigger) continue;
            if (ev.playOn === 'decisionEntry' && ev.repeatable) continue;
            if (state.player.flags[ev.id] && !ev.repeatable) continue;
            // 带 location 的 trigger＝「某时间段去某地点遇到某人」，不进日历扫描（2026-10-13 茄子定「地点事件必须到访才触发」）。
            // 到访那两侧才是它的派发口：地图地点 `meetEventAtLocation`（咖啡厅）、公园掷偶遇 `EncounterSystem.parkMeetEvent`。
            // 挂在扫描侧等于把「去不去」这条尺收走——实测过：不筛就每月自己入队。
            if (ev.trigger.location) continue;
            if (!this.triggerMet(ev.trigger)) continue;
            this.fire(ev);
        }

        // ②' 数值事件（2026-10-22 用户令「先按名气／咖位掷骰决定是否触发，再从符合门槛的事件抽取」）
        this.rollNumericEvent();

        // ③ message 拍统一投递（2026-10-14 茄子定「message 分类的剧情=人脉页聊天页发的消息」
        // 且「月底统一发送」）：月底结算此刻把队列里所有停在 message 拍的条目整批送进聊天存根，
        // 月度执行的演出档从此不碰 message（不弹卡、不推进）。非 message 拍（encounter/media）留给演出档。
        this.sweepChatDelivery();
    }

    /**
     * **数值事件的月度判定：两道闸**（2026-10-22 用户令「先按名气／咖位掷骰决定是否触发，再从符合门槛的事件抽取」）。
     *
     * ① **总闸**＝`GameConfig.NUMERIC_EVENT_RANK_CHANCE[咖位-1]`：这一下决定"这个月有没有事"。
     *    Lv.1~3 给 0 ⇒ 还没人盯你，数值事件不演。
     * ② **池内抽取**＝门槛全满足的事件里按 `trigger.weight` 轮盘抽一条 ⇒ **每月至多一条是结构性的**。
     *
     * 为什么改成两道闸（旧口径的实测代价）：单条事件各自掷概率时，"一个月至多一条"要靠名额互抢，
     * 表内顺序会把后面的事件压扁（三条 0.40/0.40/0.35 落地只有 40%／24%／12.6%），调数值时读不出真实意图；
     * 拆成"有没有"＋"哪一条"之后，表内顺序不再影响各条的相对几率，权重怎么改就是怎么算。
     * 顺序上**先组池再掷闸**：池空就不掷（省一次随机，也不会出现"掷中了却没戏可演"）。
     *
     * 池里**只收 `repeatable: true` 的事件**——一次性事件是特殊事件语义（门槛满足即必发、演完置灯、
     * 不占这里的名额与轮盘），走上面 ② 的全局扫描（2026-10-26 茄子令「一次性事件不应该占重复事件的机会」）。
     * 不做这层分离的话，一次性事件会先稀释轮盘分母、触发退池后剩余事件占比又全体上浮＝概率随进程漂移。
     */
    private rollNumericEvent(): void {
        const state = GameState.getInstance();
        const gate = GameConfig.NUMERIC_EVENT_RANK_CHANCE[state.player.rank - 1] ?? 0;
        if (gate <= 0) return;

        const pool: { ev: FixedEventDef; weight: number }[] = [];
        for (const ev of GameData.FIXED_EVENTS) {
            if (ev.playOn !== 'decisionEntry' || !ev.trigger || !ev.repeatable) continue;   // 一次性走 ② 必发
            if (ev.trigger.location) continue;                 // 地点事件到访才派发（同 ②）
            if (!this.triggerMet(ev.trigger)) continue;
            const weight = this.weightOf(ev.trigger, state.getAttribute('reputation'));
            if (weight <= 0) continue;                         // 权重 0 ＝ 这一档不抽
            pool.push({ ev, weight });
        }
        if (pool.length === 0) return;
        if (Utils.randomFloat(0, 1) >= gate) return;           // 总闸没过：本月无事

        let rest = Utils.randomFloat(0, pool.reduce((s, p) => s + p.weight, 0));
        let pick = pool[pool.length - 1].ev;                   // 浮点尾差兜底（取池尾，绝不落空）
        for (const p of pool) {
            if (rest < p.weight) { pick = p.ev; break; }
            rest -= p.weight;
        }
        this.fire(pick);
    }

    /** 池内权重：数字直接取；档位表按口碑取档（一档都没命中＝0 ＝ 不抽） */
    private weightOf(t: EventTrigger, reputation: number): number {
        const spec = t.weight;
        if (spec === undefined) return 1;
        if (typeof spec === 'number') return spec;
        return bandOf(spec, reputation)?.weight ?? 0;
    }

    /**
     * 月初全局扫描（2026-10-17 茄子令「时间线文档写的月份＝游戏里出现的月份」）。
     * 为什么需要它：`checkPending` 在 `doFinishMonth` 尾巴跑，紧跟 `advanceMonth()`，所以日历事件
     * 会被推到**下个月**才上屏（女团解散实测踩到：写 Y2M4 → 五月才演）。本口在玩家点「确认行程」
     * （＝月之初，`GameLoop.confirmMonth`）扫一遍日历事件，让「写的那个月」就是「上屏的那个月」。
     *
     * 边界：**只认带 `trigger.calendar` 的事件**——deferred 延迟队列、message 月底投递、非日历 trigger
     * （stage 等）一律留在 `checkPending`，行为不变；带 `location` 的照旧跳过（到访才派发）。
     * 与月底扫描不冲突：`fire` 对「已触发 flag / 已在队」幂等，重复扫不会攒出重复条目。
     */
    checkCalendarAtMonthStart(): void {
        const state = GameState.getInstance();
        for (const ev of GameData.FIXED_EVENTS) {
            if (!ev.trigger?.calendar) continue;   // 非日历事件不归本口管
            if (state.player.flags[ev.id] && !ev.repeatable) continue;
            if (ev.trigger.location) continue;     // 地点事件到访才触发（同 checkPending）
            if (this.triggerMet(ev.trigger)) this.fire(ev);
        }
    }

    /**
     * 决策日入场扫描（2026-10-21）：只认阶段审查演出宿主（`playOn: 'stageStory'`）的事件。
     * 为什么单开一口：日历扫描在点「确认行程」时才跑，而审查戏要排在玩家规划这个月之前；
     * 也只扫本宿主——别的宿主有自己的入场时机（决策页在点确认后、执行页在每周叙事读毕），
     * 在这里抢跑会把它们的戏提前播掉。入队幂等，重复扫不会攒条目。
     */
    checkStageStory(): void {
        const state = GameState.getInstance();
        for (const ev of GameData.FIXED_EVENTS) {
            if (ev.playOn !== 'stageStory') continue;
            if (!ev.trigger) continue;                 // 无 trigger＝显式派发（如失败演出）
            if (state.player.flags[ev.id] && !ev.repeatable) continue;
            if (ev.trigger.location) continue;         // 地点事件到访才触发（同 checkPending）
            if (this.triggerMet(ev.trigger)) this.fire(ev);
        }
    }

    /** 月底 message 投递扫描：逐条遍历（⛔ 只看队头——演到一半的序列挂在队头会挡住后面的单体消息），
     *  当前拍是 message 就投一拍推进一拍，连排的 message 步一次送完；走到非 message 拍即停（留给演出档）。 */
    private sweepChatDelivery(): void {
        for (const pending of [...this.pendingMessages()]) {
            const ev = this.eventById(pending.eventId);
            if (!ev) continue;
            for (let guard = 0; guard < 40; guard++) {
                const msg = this.entryOf(pending.eventId);
                if (!msg || msg.resolved) break;
                const step = ev.steps?.[msg.stepIndex];
                const medium = step ? step.medium : ev.medium;
                if (medium !== 'message') break;
                this.deliverAsChat(pending.eventId);
                this.advancePlot(pending.eventId);
            }
        }
    }

    /**
     * message 拍投递（sweep 专用口）：结算闸门（`deliver`，只在未交付时走——序列里 media 步演出
     * 可能已把整条交付过）＋把当前拍文案落进聊天存根 `player.plotChats`。
     * 文案按行拆成多颗气泡、剥「NPC名：」前缀、做 {player} 插值。
     * ⛔ 幂等不许看 msg.read——那是整条事件的交付标记，连排 message 步投第二拍时它早已置真；
     * 防重复投靠 sweep 的「投一拍推进一拍」结构与 fire 的一次性入队。
     */
    private deliverAsChat(eventId: string): void {
        const state = GameState.getInstance();
        const msg = this.entryOf(eventId);
        const ev = this.eventById(eventId);
        if (!msg || !ev) return;
        if (!msg.read) this.deliver(eventId);            // 结算闸门（flag/meetNpcs/首拍效果）
        const step = ev.steps?.[msg.stepIndex];
        const raw = ((step ? step.description : ev.description) ?? '').trim();
        const nick = (state.player.nickname ?? '').trim();
        const applied = raw.split('\n').map(l => l.trim()
            .split('%玩家名%').join(state.player.name || '你')   // 本名（正式报名用；{player} 优先昵称）
            .split('{player}').join(nick || state.player.name || '你')).filter(Boolean);
        const npcId = step?.npcId ?? ev.npcId ?? 'npc_assistant';
        const npcName = plotNpcName(npcId);
        const abs = state.player.year * 12 + state.player.month;
        const rawChoices = step ? step.choices : ev.choices;
        const choices = rawChoices?.map(c => c.text) ?? [];
        // reply/setFlags/effects 三张表一律与 choices 同下标（2026-10-15 修：旧写法 `.map().filter(非空)`
        // 会把空位挤掉，两条回复里只有第二条带 effects 时，那条账就记到了第一条头上——
        // 「不同回复加的不同」正是接下来要用的形状，先把下标钉回一一对应）
        const replies = rawChoices?.map(c => c.reply) ?? [];
        const choiceFlags = rawChoices?.map(c => c.setFlags) ?? [];
        const choiceEffects = rawChoices?.map(c => c.effects) ?? [];
        if (!Array.isArray(state.player.plotChats)) state.player.plotChats = [];
        let lastIdx = -1;
        for (const line of applied) {
            // 「名字：」前缀是 VN 演出的署名语法，聊天气泡本就在发信人的会话里——剥掉
            const m = npcName ? line.match(new RegExp(`^${npcName}：`)) : null;
            const text = m ? line.slice(m[0].length).trim() : line;
            if (!text) continue;
            state.player.plotChats.push({ npcId, text, absMonth: abs, read: false });
            lastIdx = state.player.plotChats.length - 1;
        }
        // choices 及其 reply/flags/effects 挂在最后一条气泡上（NPC 话说完才有回复；聊天气泡流里不画按钮，
        // 这颗存根＝聊天页底部待办清单的那一行，见 CharactersPanel.renderChatOptions，2026-10-15 茄子定乙档）
        if (choices.length && lastIdx >= 0) {
            const chat = state.player.plotChats[lastIdx];
            chat.choices = choices;
            chat.replies = replies;
            chat.choiceFlags = choiceFlags;
            chat.choiceEffects = choiceEffects;
        }
        // 这一拍自己的账：算好随气泡落档（渲染时不许反查表，表以后改了老气泡不许跟着变）。
        // 带回复的拍要等玩家选定回复才落（不选＝不加，红点一直挂着＝这笔账还没领）；
        // 不带回复的拍投递即落。两种都落账之后才在气泡下显形（2026-10-15 茄子定）。
        if (lastIdx >= 0) {
            const list = beatEffects(ev, step, msg.scriptId);
            if (list.length) {
                state.player.plotChats[lastIdx].effects = list;
                if (!choices.length) this.applyBeatEffects(list);
            }
        }
        EventBus.emit(GameEvents.STATE_CHANGED);
    }

    /**
     * 玩家选定某颗剧情气泡的回复（聊天页那行的唯一落账口，UI 只调这一个）。
     * 定格 `pickedChoice`，再落这一拍欠着的账（`effects`），最后落所选那条回复自己的 effects/flags。
     * 幂等：已答过的存根直接返回（旧档 pickedChoice 已定的不许重放）。
     */
    pickChatReply(chat: PlotChat, choiceIdx: number): void {
        if (!chat || chat.pickedChoice !== undefined) return;
        const state = GameState.getInstance();
        chat.pickedChoice = choiceIdx;
        // 清单不清空：它与所选那条的账一起，就是气泡下那枚变动行的数据源（显形靠 pickedChoice 判，不靠字段有无）
        if (chat.effects?.length) this.applyBeatEffects(chat.effects);
        chat.choiceFlags?.[choiceIdx]?.forEach(f => state.setFlag(f));
        if (chat.choiceEffects?.[choiceIdx]) eventApplyEffects(chat.choiceEffects[choiceIdx]);
        EventBus.emit(GameEvents.STATE_CHANGED);
    }

    // ===== 聊天页读取口（CharactersPanel） =====

    /** 一颗剧情聊天存根算不算「还有事没办完」（2026-10-15 茄子定乙档）：没读过，或
     *  **有回复选项而那句回复还没发**。乙档把选项从气泡流收进底部清单，进过聊天页只算"读过了"，
     *  不算"答完了"——红点必须留到那句回复真发出去，否则玩家一开门就把待办读没了（看不见＝没做）。
     *  三处读数（NPC 行角标 / 人脉 tab 胶囊 / 进页清账）共用这一把尺，禁止 各写一遍。 */
    static isPendingChat(c: PlotChat): boolean {
        return !c.read || (!!c.choices?.length && c.pickedChoice === undefined);
    }

    /** 某NPC的剧情聊天消息（按时间序；回放与红点共用） */
    chatMessagesFor(npcId: string): PlotChat[] {
        return (GameState.getInstance().player.plotChats ?? [])
            .filter(c => c.npcId === npcId)
            .sort((a, b) => a.absMonth - b.absMonth);
    }

    /** 某NPC未读剧情消息数 */
    unreadChats(npcId: string): number {
        return this.chatMessagesFor(npcId).filter(c => PlotSystem.isPendingChat(c)).length;
    }

    /** 全部未读剧情消息数（底部「人脉」tab 朱红胶囊的合计项，2026-10-14 茄子令「人脉页本身就有未读消息红点」并入） */
    totalUnreadChats(): number {
        return (GameState.getInstance().player.plotChats ?? []).filter(c => PlotSystem.isPendingChat(c)).length;
    }

    /** 进聊天页即已读（tab 胶囊计数随之回落）；没答的回复选项仍算待办，回落不到零 */
    markChatsRead(npcId: string): void {
        let changed = false;
        for (const c of this.chatMessagesFor(npcId)) {
            if (!c.read) { c.read = true; changed = true; }
        }
        if (changed) EventBus.emit(GameEvents.STATE_CHANGED);
    }

    /** 剧本生命周期信号 → 查附表 plotHooks → 立即/延迟触发 */
    private onSpecialSignal(when: 'cast' | 'done' | 'airStart' | 'rating', scriptId: string): void {
        const sp = GameData.SPECIAL_SCRIPTS.find(s => s.scriptId === scriptId);
        if (!sp) return;
        const state = GameState.getInstance();
        const absMonth = state.player.year * 12 + state.player.month;
        for (const hook of sp.plotHooks.filter(h => h.when === when)) {
            if (hook.afterMonths) {
                this.deferred.push({ eventId: hook.event, dueAbsMonth: absMonth + hook.afterMonths, hook });
            } else {
                // 立即触发：cond 直接判定（不满足默认作废；wait 无延迟场景等价于不触发）
                if (this.condMet(hook.cond, hook.event)) {
                    const ev = this.eventById(hook.event);
                    if (ev) this.fire(ev);
                }
            }
        }
    }

    /** 全局 trigger 判定：全部满足才 true */
    private triggerMet(t: EventTrigger): boolean {
        const state = GameState.getInstance();
        if (t.calendar) {
            const y = state.player.year + 1, m = state.player.month;   // 展示口径 1-indexed 起
            const fromM = t.calendar.from.month ?? 1, toM = t.calendar.to.month ?? 12;
            const inFrom = y > t.calendar.from.year || (y === t.calendar.from.year && m >= fromM);
            const inTo = y < t.calendar.to.year || (y === t.calendar.to.year && m <= toM);
            if (!(inFrom && inTo)) return false;
        }
        if (t.require && !this.requireMet(t.require)) return false;
        if (t.rankBelow !== undefined && state.player.rank >= t.rankBelow) return false;
        if (t.scriptAirStart && !this.isScriptAiring(t.scriptAirStart)) return false;
        if (t.requireFlags && !t.requireFlags.every(f => state.player.flags[f] === true)) return false;
        return true;
    }

    /**
     * **数值门槛**（`EventRequire`，所有触发口径共用的一套判据）：全满足才算过。
     * 维度＝八维属性（含口碑）＋ 咖位／阶段／名气／粉丝／资金／NPC 好感／标签。
     */
    private requireMet(r: EventRequire): boolean {
        const state = GameState.getInstance();
        for (const [key, min] of Object.entries(r.attr ?? {}) as [AttributeKey, number][]) {
            if (state.getAttribute(key) < min) return false;
        }
        for (const [key, max] of Object.entries(r.attrMax ?? {}) as [AttributeKey, number][]) {
            if (state.getAttribute(key) > max) return false;
        }
        if (r.rank !== undefined && state.player.rank < r.rank) return false;
        if (r.rankMax !== undefined && state.player.rank > r.rankMax) return false;
        if (r.stage !== undefined && state.currentStage < r.stage) return false;
        if (r.fame !== undefined && state.getFame() < r.fame) return false;
        if (r.fans !== undefined && (state.player.fans ?? 0) < r.fans) return false;
        if (r.money !== undefined && (state.player.resources?.money ?? 0) < r.money) return false;
        if (r.favor) {
            for (const [npcId, need] of Object.entries(r.favor)) {
                if (state.getFavor(npcId) < need) return false;
            }
        }
        if (r.tags && !r.tags.every(tag => state.player.tags.includes(tag))) return false;
        return true;
    }

    /** 玩家是否在某部剧里有合同（演过，含已杀青/在播）——`FixedStep.onlyIfCast` 的步级门槛 */
    isPlayerInScript(scriptId: string): boolean {
        return (GameState.getInstance().player.contracts ?? []).some(c => c.scriptId === scriptId);
    }

    /**
     * 某部剧是否「在播」（与玩家是否参演无关）：2026-10-18 加，配合 `trigger.scriptAirStart`。
     * 两条来源——玩家在播合同（带剧级档期门控，与 `HeatChartSystem.collectAiring` / `AwardSystem.allWorks`
     * 同一把尺：配角先杀青、status 早翻 airing，但全剧没杀青＝还没开播）＋ NPC 在途工作 `phase='airing'`。
     */
    private isScriptAiring(scriptId: string): boolean {
        const state = GameState.getInstance();
        const nowAbs = state.player.year * 12 + state.player.month;
        const script = GameData.SCRIPT_MAP[scriptId];
        for (const c of state.player.contracts ?? []) {
            if (c.scriptId !== scriptId || c.status !== 'airing') continue;
            // 「在播」判据（2026-10-29 甲案，茄子定调）：开播月整月=待播宣传月，剧在开播月**月末**
            // 首滴时才上线，nowAbs > 开播月才算在播——scriptAirStart 剧情钩子（901 热爆/902 新闻卡）
            // 随之在开播次月月初触发（plotHook(airStart) 侧本就挂在首滴那一刻=上线当刻，不判此函数）。
            // ⛔ 误用 isDramaAired（>= 开播月）：那是剧宣窗口口径，开播月要留给玩家发待播宣传。
            if (script && nowAbs > dramaTimelineOf(c, script, nowAbs).premiere) return true;
        }
        for (const npc of normalNpcSystem.getNpcs()) {
            // NPC 侧同尺：新钟 job 开播月⛔在播（次月起算）；旧档 job 无 shootStartAbs 走旧钟
            // （杀青即开播），不设门
            if ((npc.activeJobs ?? []).some(j => j.scriptId === scriptId && j.phase === 'airing'
                && (j.shootStartAbs == null || scriptPremiereAbsOf(j.shootStartAbs, script) < nowAbs))) return true;
        }
        return false;
    }

    /**
     * 入队时的起始拍：跳过开头门槛不满足的步（requireFlags / onlyRole / onlyIfCast）。
     * 场景（2026-10-18）：热爆步骤 1 热议帖只在玩家参演时演、步骤 2 新闻卡全网——没参演的玩家
     * 必须从步骤 2 开始；`advancePlot` 只判「后续步」，不处理首拍，所以起始指针要在入队时算好。
     */
    private firstPlayableStep(ev: FixedEventDef): number {
        const steps = ev.steps ?? [];
        if (!steps.length) return 0;
        const state = GameState.getInstance();
        const myRole = playedRoleInEvent(ev);
        let i = 0;
        while (i < steps.length) {
            const st = steps[i];
            if (st.requireFlags && !st.requireFlags.every(f => state.player.flags[f] === true)) { i++; continue; }
            if (st.onlyRole && !roleMatch(st.onlyRole, myRole)) { i++; continue; }
            if (st.onlyIfCast && !this.isPlayerInScript(st.onlyIfCast)) { i++; continue; }
            break;
        }
        return i;
    }

    /** 钩子 cond 判定（字段缺省=不检查） */
    private condMet(cond: PlotHook['cond'], _eventId: string): boolean {
        if (!cond) return true;
        const state = GameState.getInstance();
        if (cond.heat !== undefined || cond.rating !== undefined) {
            // 热度/评分条件读"信号剧本"的最新值——钩子事件名前缀反查剧本（FIXED_901_BOOM → SCRIPT_901）
            const scriptId = _eventId.startsWith('FIXED_')
                ? `SCRIPT_${_eventId.slice(6, 9)}` : undefined;
            const script = scriptId ? GameData.SCRIPT_MAP[scriptId] : undefined;
            if (!script) {
                // 反查不到剧本＝这本账读不出来。旧写法在这里先取 script.id 就抛（月末结算整批挂），
                // 兜底要响：喊出来 + 按「条件不满足」处理（查无凭据不许放行）。
                console.warn(`[PlotSystem] 钩子 ${_eventId} 带 cond ${JSON.stringify(cond)}，但按事件名前缀反查（${scriptId ?? '不合 FIXED_xxx 式'}）在剧本表查无，所以这一发不演（请查 SpecialScripts 的 event 命名）`);
                return false;
            }
            if (cond.heat !== undefined) {
                // 热度 = 剧本基础热度 + plotHeatBonus（剧情热度加成）+ heatPromoted（剧宣/客串热度加成）
                // 两本账都要读，只读一本会漏掉另一路（与 FanSystem.workHeatOf 的口径差异＝本档不动数值，待拍）
                const plotBonus = state.player.plotHeatBonus?.[script.id] ?? 0;
                const promo = state.player.heatPromoted?.[script.id] ?? 0;
                if (script.heat + plotBonus + promo < cond.heat) return false;
            }
            if (cond.rating !== undefined) {
                // 实际评分在履历里；简化读 baseScore + 播出加成需 ReviewSystem——批 3 接实际评分，先按 baseScore 判
                if (script.baseScore < cond.rating) return false;
            }
        }
        if (cond.rank !== undefined && state.player.rank < cond.rank) return false;
        if (cond.fame !== undefined && state.getFame() < cond.fame) return false;
        if (cond.favor) {
            for (const [npcId, need] of Object.entries(cond.favor)) {
                if (state.getFavor(npcId) < need) return false;
            }
        }
        if (cond.flags && !cond.flags.every(f => state.player.flags[f] === true)) return false;
        return true;
    }

    /**
     * 派发＝只入队（2026-10-13 形状 C 收口，此前是形状 B「效果先结算、句子后播」）。
     * 三个静默出口（false＝没入队，调用方不许消耗任何已预扣的状态）：
     *  ①一次性：事件已交付过（flag 由 `deliver` 置，不是这里）；
     *  ②初遇判据（meetNpcs 事件专属）：只看**主 NPC**（npcId ?? meetNpcs 首个）——多目标事件里配角
     *    已结识 不许枪毙整场初遇；主 NPC 已结识 = 都认识了没理由再演，跳过（下一候选/时期接管）。
     *    2026-10-14 收窄到 meetNpcs 事件：续接类事件（试镜通知/杀青钩子…）的 npcId 必然已结识
     *    （初遇完成才有后续），旧写法把它们全枪毙在门口——实测试镜通知永远收不到。
     *  ③已在队：同 eventId 还挂着未演完的条目，所以不许重复入队（月度扫描每月都会重扫同一条 trigger，
     *    没有这道筛就会攒出一叠重复剧情）。
     * 入队本身安全＝条目落在 `player.plotMessages`（存档内），刷新后仍在队里、终会被演到，
     * 调用方因此可以放心把它当「派发成功」的信号（旧用途＝偶遇首拍周保底盖章，该机制 2026-10-16 已撤）。
     */
    /** ctx（2026-10-24 通告事件批）：通告钩子带入的剧本上下文——落 PlotMessage 供插值，不传＝普通 fire */
    private fire(ev: FixedEventDef, ctx?: { scriptId?: string; roleIndex?: number }): boolean {
        const state = GameState.getInstance();
        if (state.player.flags[ev.id] && !ev.repeatable) return false;    // 一次性保险（repeatable 不受）
        if (ev.meetNpcs?.length) {
            const lead = ev.npcId ?? ev.meetNpcs[0];
            if (lead && state.getFavor(lead) >= 0) return false;   // 主 NPC 已结识 → 初遇不再演
        }
        if (!Array.isArray(state.player.plotMessages)) state.player.plotMessages = [];
        const msgs = state.player.plotMessages;
        // 在队查重按「事件 + 剧」双键（2026-10-24 通告事件批）：通用事件一个 id 服务所有通告，
        // 同月杀青两部剧＝两条各自入队；不带 ctx 的老语义不变（同 eventId 在队即重复）
        if (msgs.some(m => m.eventId === ev.id && !m.resolved && (m.scriptId ?? undefined) === (ctx?.scriptId ?? undefined))) return false;
        const abs = state.player.year * 12 + state.player.month;
        msgs.push({
            eventId: ev.id,
            npcId: ev.npcId ?? (ev.medium === 'message' ? 'npc_assistant' : undefined),
            scriptId: ctx?.scriptId,
            roleIndex: ctx?.roleIndex,
            read: false,
            resolved: false,
            stepIndex: this.firstPlayableStep(ev),   // 首拍门槛不满足时从下一拍开始（见 firstPlayableStep）
            pushedAbsMonth: abs,
        });
        EventBus.emit(GameEvents.STATE_CHANGED);
        return true;
    }

    /**
     * 交付点：UI 把这条剧情的第一句交到玩家眼前那一刻调（与偶遇 `claim` 同轴）。
     * 结算三件事一次做完：置一次性 flag、按 `meetNpcs` 结识、执行首拍效果（无 steps 走事件级兜底入口）
     * ——结识与数值都从 `beatEffects` 那一份清单走，所以配了的账一定和变动行上屏的一字不差。
     * 幂等：已交付过直接返回，不许二次发钱/二次结识。
     */
    deliver(eventId: string): boolean {
        const state = GameState.getInstance();
        const msg = this.entryOf(eventId);
        if (!msg) {
            console.warn(`[PlotSystem] 交付一个不在队里的事件 ${eventId}，所以跳过（数据错配态）`);
            return false;
        }
        if (msg.read) return true;                       // 已结算，重复交付幂等
        const ev = this.eventById(eventId);
        if (!ev) {
            console.warn(`[PlotSystem] ${eventId} 在 FIXED_EVENTS 查无，所以不许结算（条目留在收件箱里等数据补齐）`);
            return false;
        }
        if (!ev.repeatable) state.setFlag(ev.id);    // 可重复的事件不置灯（否则下次被自己挡住）
        // 结识（meetNpcs，置 0 入人脉页）＋首拍数值：同一份 `beatEffects` 清单逐条落账（2026-10-15 收口，
        // 与演出侧的变动行同源——旧写法在这里单写一遍结识循环，数值却只有循环外那份，两边会分叉）。
        // 首拍取 `msg.stepIndex`：首拍被 onlyIfCast/onlyRole/requireFlags 跳过时，起始指针已在入队时前移。
        this.applyStepEffects(ev, ev.steps?.[msg.stepIndex], msg.scriptId);
        msg.read = true;
        EventBus.emit(GameEvents.STATE_CHANGED);
        return true;
    }

    /** 未演完的条目（含演到一半的）＝演出队列，先入先演；不许带副作用 */
    pendingMessages(): PlotMessage[] {
        const list = GameState.getInstance().player.plotMessages ?? [];
        return list.filter(m => !m.resolved).sort((a, b) => a.pushedAbsMonth - b.pushedAbsMonth);
    }

    /**
     * 按演出宿主取队列那一片（2026-10-14 茄子令「爆火剧情挪到点击确认行程之后、在决策页展示」）：
     * 场地写在事件表 `playOn` 上，缺省＝执行档；表内查无的条目也归执行档——那边的演出档会
     * `warnUnplayable(..., '表内查无')` 点名，不许在两个宿主之间来回挂空（缺省只在这一处判，宿主各写一遍就是第二把尺）。
     */
    pendingFor(host: PlotHost): PlotMessage[] {
        return this.pendingMessages().filter(m => this.hostOf(m.eventId) === host);
    }

    /** 这一条该在哪个宿主演（读表现算，不落存档） */
    private hostOf(eventId: string): PlotHost {
        return this.eventById(eventId)?.playOn ?? 'execution';
    }

    /**
     * 按 eventId 取「这次要动的那条」队条目：**优先未演完的那条**。
     *
     * 为什么不能 `.find(m => m.eventId === id)` 取第一条（2026-10-23 实测「数值事件弹卡后点空白关不掉」）：
     * resolved 的条目演完不从队里删（历史留在存档里），可重复事件（数值事件全部 repeatable）第二次入队时
     * 必然排在旧的那条**后面**。旧写法永远捞到旧的已完结条目 ⇒ `deliver` 幂等早退（第二次的账一分不落）、
     * `advancePlot` 判 `msg.resolved` 早退（新条目永远走不完）⇒ 卡片一点空白就关、下一秒又原地重开，看着＝卡死。
     * 同 id 同时最多只有一条未演完（`fire` 的「已在队」闸），所以未演完的那条就是这次演的那条。
     * 没有未演完的条目时回落到任意一条（`deliver` 的幂等语义、聊天拍对老条目的读取）。
     */
    private entryOf(eventId: string): PlotMessage | undefined {
        const list = GameState.getInstance().player.plotMessages ?? [];
        return list.find(m => m.eventId === eventId && !m.resolved)
            ?? list.find(m => m.eventId === eventId);
    }

    /**
     * 整条都演不了的（`media` 载体，等弹窗 UI 那批）：当场交付＋把序列一次走完。
     * 不许因为界面还没接就把玩家的账悬在半空——`FixedEvents.ts` 里 media 那条链（如 FIXED_901_BOOM 的
     * 热爆叙述拍）本来就靠事件级 setFlags/unlockScripts 解锁后续剧本，改成交付制后若不排干，剧本会永远不出现（＝回归）。
     * 「内容玩家没见过」这件事由渲染侧的 warn 与读表哨兵负责喊，不许在这里静默省钱。
     */
    deliverAndDrain(eventId: string): void {
        if (!this.deliver(eventId)) return;
        // 兜底要响——但只在该响的出口响（2026-11-03 从 `applyStepEffects` 挪上来）：
        // 正常演出路径的 media 卡有偏白气泡承载数值（`plotPlayback` 的 media 分支，2026-10-22 茄子令「不出对话框也能承载数值变化」），
        // 唯独这条「界面还没接就整条排干」的路玩家一个字都没见到。写在 applyStepEffects 里＝每支带数值的 media 事件都误报一遍。
        const drained = this.eventById(eventId);
        const ctxId = this.entryOf(eventId)?.scriptId;
        if (drained && !hiddenBeatWarned.has(eventId)
            && (drained.steps ?? [null]).some(s => beatEffects(drained, s, ctxId).some(e => e.kind !== 'meet'))) {
            hiddenBeatWarned.add(eventId);
            console.warn(`[PlotSystem] ${eventId} 走的是 deliverAndDrain（没有演出通道），effects/scriptHeat 配了也上不了屏，玩家读不见这笔变动`);
        }
        for (let guard = 0; guard < 40; guard++) {
            const msg = this.entryOf(eventId);
            if (!msg || msg.resolved) return;
            this.advancePlot(eventId);
        }
        console.warn(`[PlotSystem] ${eventId} 序列超过 40 拍仍未走完，所以停在这里（数据异常态，请查 steps 配置）`);
    }

    /**
     * 事件/步的效果执行（2026-10-15 收口）：数值与结识一律从 `beatEffects` 那一份清单走，
     * 本方法只做两件事——message 拍的账让给聊天投递那侧、flag 落账（非数值、不上变动行）。
     * 步级只吃该步自己的字段（有 steps 时事件级数值一律不算，与旧写法同口径）。
     * 「数值有没有上屏」不归这里管：media 卡的变动由偏白气泡承载（`plotPlayback`），
     * 唯一没有演出通道的出口是 `deliverAndDrain`，喊话挂在那一处。
     */
    private applyStepEffects(ev: FixedEventDef, step?: FixedStep, ctxScriptId?: string): void {
        const state = GameState.getInstance();
        const medium = step ? step.medium : ev.medium;
        // message 拍（＝人脉页那颗气泡）的账不在这里落：无回复的投递那一刻落、带回复的玩家选定回复才落，
        // 见 `deliverAsChat` / `pickChatReply`。走进那一拍就把钱扣走＝信还没送到，账先清了（2026-10-15 茄子定）。
        const list = medium === 'message' ? [] : beatEffects(ev, step, ctxScriptId);
        this.applyBeatEffects(list);
        if (step) {
            (step.setFlags ?? []).forEach(f => state.setFlag(f));
            return;
        }
        (ev.setFlags ?? []).forEach(f => state.setFlag(f));
        (ev.unlockScripts ?? []).forEach(id => state.unlockScript(id));
    }

    /** 逐条落账（清单＝`beatEffects` 原样，判据不许在这里改）：先结识、后数值 */
    private applyBeatEffects(list: PlotBeatEffect[]): void {
        const state = GameState.getInstance();
        for (const e of list) {
            switch (e.kind) {
                case 'meet':
                    if (state.getFavor(e.id) >= 0) break;      // 已结识不许二次结识
                    state.meetNpc(e.id);
                    break;
                case 'attr': eventApplyEffects({ [e.id]: e.value }); break;
                case 'favor': state.addFavor(e.id, e.value); break;
                case 'scriptHeat': this.addScriptHeat(e.id, e.value); break;
            }
        }
    }

    /** 剧热度加值：写 player.plotHeatBonus（2026-10-10 茄子定：剧情热度不走剧宣账本、无封顶；
     *  FanSystem.workHeatOf 叠加读取） */
    private addScriptHeat(scriptId: string, delta: number): void {
        const state = GameState.getInstance();
        if (!GameData.SCRIPT_MAP[scriptId]) return;
        if (!state.player.plotHeatBonus) state.player.plotHeatBonus = {};
        state.player.plotHeatBonus[scriptId] = (state.player.plotHeatBonus[scriptId] ?? 0) + delta;
    }

    // ===== UI 调用入口（演出通道＝`src/ui/plotStage.ts`，交付＝`deliver`） =====

    /** 取事件定义 */
    eventById(id: string): FixedEventDef | undefined {
        return GameData.FIXED_EVENTS.find(e => e.id === id);
    }

    /** trigger 判定公共口（自由出行批：公园偶遇评估特殊 NPC 初遇候选的时间窗用；location 由调用方自比） */
    isTriggerMet(t: NonNullable<FixedEventDef['trigger']>): boolean {
        return this.triggerMet(t);
    }

    /** 指名派发（外部系统入口：偶遇初遇等"抽中即点名"的场景）。
     *  2026-10-13 形状 C 起本函数＝入队（不许结算），一次性 flag 由 `deliver` 在演出交付那刻置。
     *  返回 false＝没入队（已交付过／主 NPC 已结识／同一条还在队里／表内查无），
     *  调用方不得据此消耗任何预扣状态（旧例＝偶遇首拍周保底旗标 encounterGranted，2026-10-16 已随保底一起撤）。 */
    fireByEvent(eventId: string): boolean {
        const ev = this.eventById(eventId);
        return ev ? this.fire(ev) : false;
    }

    /**
     * 地点触发查询（2026-10-10：自由出行地图接线——"某时间段去某地点遇到某人"）：
     * 扫特殊 NPC 附表，返回一位未结识 NPC 在该地点的初遇事件（meet.events 候选按序取
     * 第一个"未触发、未排队且 trigger 满足（含 location/calendar）"的）；无可遇返回 null。
     * 调用方（地图地点访问）拿到后 fireByEvent(eventId) 派发。
     * 注意：排除「已在队未演完」的条目：否则同一次到访会捞到玩家上次没演完的同一拍、白扣行动点。
     */
    meetEventAtLocation(loc: string): { npcId: string; eventId: string } | null {
        const state = GameState.getInstance();
        const pendingIds = new Set(this.pendingMessages().map(m => m.eventId));
        for (const sp of GameData.SPECIAL_NPCS) {
            if (state.getFavor(sp.npcId) >= 0) continue;   // 已结识不再初遇
            for (const id of sp.meet.events) {
                if (state.player.flags[id] || pendingIds.has(id)) continue;
                const ev = this.eventById(id);
                if (!ev?.trigger?.calendar) continue;      // 初遇候选必须带时间窗
                if (ev.trigger.location !== loc) continue;
                if (!this.isTriggerMet(ev.trigger)) continue;
                return { npcId: sp.npcId, eventId: id };
            }
        }
        return null;
    }

    /**
     * 当前步带选项时由 UI 调：应用选项效果/置 flag → 推进到下一步（跳过 requireFlags 不满足的步）；
     * 序列走完 → resolved。无选项直接调 advancePlot()。
     */
    choosePlot(eventId: string, choiceIndex: number): void {
        const state = GameState.getInstance();
        const msg = this.entryOf(eventId);
        const ev = this.eventById(eventId);
        if (!msg || !ev || msg.resolved) return;
        const steps = ev.steps ?? [];
        const step = steps[msg.stepIndex];
        const choice = (step?.choices ?? ev.choices)?.[choiceIndex];
        if (choice) {
            if (choice.effects) eventApplyEffects(choice.effects);
            (choice.setFlags ?? []).forEach(f => state.setFlag(f));
        }
        this.advancePlot(eventId);
    }

    /** 推进到下一个满足 requireFlags 的步；走完置 resolved */
    advancePlot(eventId: string): void {
        const state = GameState.getInstance();
        const msg = this.entryOf(eventId);
        const ev = this.eventById(eventId);
        if (!msg || !ev || msg.resolved) return;
        const steps = ev.steps ?? [];
        // 玩家在本剧饰演的角色名（onlyRole 过滤用：角色对得上才演该步）
        const myRole = playedRoleInEvent(ev);
        let i = msg.stepIndex + 1;
        // 单体事件（无 steps）：确认即完结
        while (i < steps.length) {
            const st = steps[i];
            if (st.requireFlags && !st.requireFlags.every(f => state.player.flags[f] === true)) { i++; continue; }
            if (st.onlyRole && !roleMatch(st.onlyRole, myRole)) { i++; continue; }   // 语法见 roleMatch
            if (st.onlyIfCast && !this.isPlayerInScript(st.onlyIfCast)) { i++; continue; }   // 没演这部剧 = 跳过该步
            break;
        }
        if (i >= steps.length) {
            msg.resolved = true;
            // 序列整体执行完毕后，应用事件级兜底效果（flags/unlock 等事件级字段）
            (ev.setFlags ?? []).forEach(f => state.setFlag(f));
            (ev.unlockScripts ?? []).forEach(id => state.unlockScript(id));
        } else {
            msg.stepIndex = i;
            this.applyStepEffects(ev, steps[i], msg.scriptId);
        }
        EventBus.emit(GameEvents.STATE_CHANGED);
    }
}

/** 与 EventSystem.applyEffects 同键表的公共入口（批 2：PlotSystem 复用；EventSystem 内部改为委托本函数） */
export function eventApplyEffects(effects: Record<string, number>): void {
    const state = GameState.getInstance();
    for (const [key, value] of Object.entries(effects)) {
        if (value === 0) continue;
        switch (key) {
            case 'charm': case 'talent': case 'temperament': case 'stamina':
            case 'eq': case 'wisdom': case 'artistry': case 'reputation':
                state.addAttribute(key as never, value);
                break;
            case 'money': state.addMoney(value); break;
            case 'energy': state.addEnergy(value); break;
            case 'fame': state.addFame(value); break;
            case 'rank': break;   // 咖位由 RankSystem 处理
            default: console.warn(`[PlotSystem] 未知效果 key: ${key}`);
        }
    }
}

/** 非 VN 拍的「数值无处上屏」点名（一次/事件，与 plotStage 的 warned 同一脾气） */
const hiddenBeatWarned = new Set<string>();

/** scriptHeat 缺省 id 但派发没带剧（＝不是通告钩子）的点名（一次/事件） */
const scriptHeatNoCtxWarned = new Set<string>();

/** 说话人/结识主体的显示名：小助理读称呼常量，其余查 NPC 主表（剧情、聊天、变动行三处同一把尺） */
export function plotNpcName(npcId: string): string {
    return npcId === 'assistant' ? ASSISTANT.name : GameData.NPC_MAP[npcId]?.name ?? npcId;
}

/**
 * `onlyRole` 表达式判定（2026-10-10 茄子定语法）：`'柳惊蛰'`＝玩家演的正是这个角色，
 * `'!柳惊蛰'`＝玩家演的不是它（含未参演——`myRole` 为 null 时正向永不命中、反向恒命中）。
 * 步级门槛（这一拍演不演）与 `roleEffects`（这一笔账记不记）共用，两处不许各写一遍取反。
 */
export function roleMatch(expr: string, myRole: string | null): boolean {
    if (!expr) return false;
    return expr.startsWith('!') ? myRole !== expr.slice(1) : myRole === expr;
}

/** 玩家在本事件关联剧中所演的角色名（未参演/查无 = null）。
 *  事件与剧的关联：从事件级或步级 `scriptHeat` 字段提取 scriptId
 *  （事件 ID ≠ 剧 ID，不能直接用 eventId 当 scriptId 查合约——2026-10-14 修复） */
export function playedRoleInEvent(ev: FixedEventDef): string | null {
    const state = GameState.getInstance();
    const scriptId = ev.scriptHeat?.[0]?.scriptId
        ?? ev.steps?.find(s => s.scriptHeat?.[0]?.scriptId)?.scriptHeat?.[0]?.scriptId;
    if (!scriptId) return null;
    const roleIndex = state.player.contracts
        .filter(c => c.scriptId === scriptId)
        .map(c => c.roleIndex)
        .find(() => true);
    return roleIndex === undefined ? null : GameData.SCRIPT_MAP[scriptId]?.roles[roleIndex]?.name ?? null;
}

/**
 * 这颗气泡落过账之后一共改了什么：这一拍欠着的账 + 所选那条回复自己的账。
 * 与 `pickChatReply` 落的必须是同一份（两处各算一遍就会有一处说话不算数），所以折回同一个清单形状。
 */
export function settledChatEffects(chat: PlotChat): PlotBeatEffect[] {
    const list = [...(chat.effects ?? [])];
    const picked = chat.pickedChoice !== undefined ? chat.choiceEffects?.[chat.pickedChoice] : undefined;
    for (const [id, value] of Object.entries(picked ?? {})) {
        if (value !== 0) list.push({ kind: 'attr', id, value });
    }
    return list;
}

/**
 * 这一拍要改的每一笔账（纯查询：不写状态、不发事件、不读运行时缓存，
 * 只从「事件表配置 + 存档」算出来，所以刷新后重新组行，变动行一字不变）。
 * 口径与旧 `applyStepEffects` 逐字对齐：有 steps 只吃该步自己的字段，无 steps 才吃事件级；
 * 结识（事件级 `meetNpcs`）只算首拍。数值为 0 的条目一律不收（旧写法落到 addFavor/addScriptHeat 也是空转）。
 */
export function beatEffects(ev: FixedEventDef, step?: FixedStep | null, ctxScriptId?: string): PlotBeatEffect[] {
    const out: PlotBeatEffect[] = [];
    // scriptHeat 的落账对象（2026-11-03）：表里写了 `scriptId` 用表里的；缺省＝**本次派发带入的那部剧**
    // （通告钩子的 payload 上下文，与 `{script}` 插值同一个数据源，禁止另起一把尺）。
    // 两头都没有就不收这笔账并喊一次：配错不能静默变成一个说不清的数。
    const heatOf = (h: { scriptId?: string; delta: number }): void => {
        const id = h.scriptId ?? ctxScriptId;
        if (!id) {
            if (!scriptHeatNoCtxWarned.has(ev.id)) {
                scriptHeatNoCtxWarned.add(ev.id);
                console.warn(`[PlotSystem] ${ev.id} 的 scriptHeat 没写 scriptId，又不是通告钩子派发（没有带入的剧），这笔热度落不下去（静态挂剧的事件请把 id 写明）`);
            }
            return;
        }
        if (h.delta !== 0) out.push({ kind: 'scriptHeat', id, value: h.delta });
    };
    if (!ev.steps?.length || ev.steps[0] === step) {
        for (const id of ev.meetNpcs ?? []) out.push({ kind: 'meet', id, value: 0 });
    }
    const push = (effects: Record<string, number> | undefined) => {
        for (const [id, value] of Object.entries(effects ?? {})) {
            if (value !== 0) out.push({ kind: 'attr', id, value });
        }
    };
    if (!step) {
        push(ev.effects);
        for (const [id, value] of Object.entries(ev.favor ?? {})) out.push({ kind: 'favor', id, value });
        (ev.scriptHeat ?? []).forEach(heatOf);
        return out;
    }
    push(step.effects);
    // 按角色叠加数值（2026-10-10 茄子定，例：爆火小助理拍——女主玩家名气+50 / 其余+30）
    if (step.roleEffects?.length) {
        const myRole = playedRoleInEvent(ev);
        for (const re of step.roleEffects) {
            if (roleMatch(re.onlyRole, myRole)) push(re.effects);
        }
    }
    for (const [id, value] of Object.entries(step.favor ?? {})) out.push({ kind: 'favor', id, value });
    (step.scriptHeat ?? []).forEach(heatOf);
    return out;
}
